<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useSessionStore } from '../stores/session'
import { useNavSignals, type BadgeTone, type NavBadge } from '../composables/useNavSignals'
import { activeNavKey, visibleNavGroups, type NavItem, type NavTone } from '../lib/navConfig'

// The app's primary navigation: the grouped Brain / Factory / Life /
// System list from `lib/navConfig`, with ⌘K search, Add to brain and the
// account footer. Rendered as the persistent left rail on desktop and,
// with `variant="drawer"`, inside the phone tab bar's Menu drawer — one
// component, so the two surfaces can never offer different entries.

withDefaults(defineProps<{ variant?: 'rail' | 'drawer' }>(), { variant: 'rail' })

const emit = defineEmits<{
  (e: 'add-to-wiki'): void
  (e: 'ask'): void
  (e: 'search'): void
  // Fired after any entry is chosen so the drawer variant can close.
  (e: 'navigate'): void
}>()

const route = useRoute()
const session = useSessionStore()
const { currentUser } = storeToRefs(session)
const signals = useNavSignals()
const { isAdmin, addToWikiActive } = signals

const groups = computed(() => visibleNavGroups(isAdmin.value))
const activeKey = computed(() => activeNavKey(route.path))

const TONE_DOT: Record<NavTone, string> = {
  brain: 'bg-brand-primary',
  factory: 'bg-factory',
  life: 'bg-life',
  system: 'bg-ink-faint',
}

const BADGE_CLASS: Record<BadgeTone, string> = {
  attention: 'bg-attention/15 text-attention',
  factory: 'bg-factory/15 text-factory',
  alert: 'bg-status-alert/15 text-status-alert',
  neutral: 'bg-surface-hover text-ink-muted',
}

function badgeOf(item: NavItem): NavBadge | null {
  return signals.badgeFor(item.badge)
}

function accessibleName(item: NavItem): string {
  const badge = badgeOf(item)
  return badge ? `${item.label}, ${badge.label}` : item.label
}

function itemClass(item: NavItem): string {
  return item.key === activeKey.value
    ? 'bg-surface-hover text-ink font-medium'
    : 'text-ink-muted hover:bg-surface-hover/60 hover:text-ink'
}

const addToWikiLabel = computed(() =>
  addToWikiActive.value ? 'Add to brain — an ingestion is in flight' : 'Add to brain',
)

function badgeClass(item: NavItem): string {
  const badge = badgeOf(item)
  return badge ? BADGE_CLASS[badge.tone] : ''
}

function onAddToWiki(): void {
  emit('add-to-wiki')
  emit('navigate')
}

function onItem(item: NavItem): void {
  if (item.action === 'log') void session.markLogSeen()
  if (item.action === 'ask') emit('ask')
  emit('navigate')
}

async function signOut(): Promise<void> {
  emit('navigate')
  await session.signOut()
}
</script>

<template>
  <nav
    aria-label="Main"
    data-testid="app-nav-rail"
    class="flex flex-col gap-4 overflow-y-auto bg-surface-rail px-3.5 py-5"
    :class="variant === 'rail' ? 'h-full w-60 shrink-0 border-r border-border-divider' : 'w-full'"
  >
    <RouterLink
      to="/"
      data-testid="go-home"
      class="flex items-center gap-2.5 rounded-lg px-2 py-1 text-ink no-underline"
      @click="emit('navigate')"
    >
      <svg width="26" height="26" viewBox="0 0 26 26" aria-hidden="true">
        <circle cx="9" cy="13" r="6" fill="none" class="stroke-brand-primary" stroke-width="2" />
        <circle cx="17" cy="13" r="6" fill="none" class="stroke-factory" stroke-width="2" />
      </svg>
      <span class="text-base font-semibold tracking-tight">superbrain</span>
    </RouterLink>

    <button
      type="button"
      data-testid="nav-search"
      class="flex min-h-10 items-center gap-2.5 rounded-[10px] border border-border-control bg-surface-raised px-3 text-left text-sm text-ink-muted"
      @click="emit('search')"
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        stroke-width="1.6"
        aria-hidden="true"
      >
        <circle cx="7" cy="7" r="4.5" />
        <path d="M10.5 10.5 14 14" />
      </svg>
      <span class="flex-1 truncate">Search or jump…</span>
      <kbd class="rounded border border-border-subtle px-1.5 font-mono text-[11px] text-ink-muted">⌘K</kbd>
    </button>

    <button
      v-if="isAdmin"
      type="button"
      data-testid="add-to-wiki"
      :title="addToWikiLabel"
      :aria-label="addToWikiLabel"
      class="flex min-h-10 items-center justify-center gap-2 rounded-[10px] bg-brand-primary px-3 text-sm font-semibold text-[#0b1f17] hover:bg-[#7fe0bb]"
      @click="onAddToWiki"
    >
      + Add to brain
      <span
        v-if="addToWikiActive"
        data-testid="add-to-wiki-indicator"
        aria-hidden="true"
        class="h-1.5 w-1.5 rounded-full bg-[#0b1f17] motion-safe:animate-pulse"
      />
    </button>

    <div v-for="group in groups" :key="group.key" class="flex flex-col gap-px" :data-testid="`nav-group-${group.key}`">
      <div
        v-if="group.label"
        class="flex items-center gap-2 px-2.5 pb-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-faint"
      >
        <span v-if="group.tone" class="h-1.5 w-1.5 rounded-full" :class="TONE_DOT[group.tone]" aria-hidden="true" />
        {{ group.label }}
      </div>
      <template v-for="item in group.items" :key="item.key">
        <button
          v-if="item.action === 'ask'"
          type="button"
          :data-testid="`nav-${item.key}`"
          :aria-label="accessibleName(item)"
          class="flex min-h-9 items-center gap-2 rounded-lg px-2.5 text-left text-sm"
          :class="itemClass(item)"
          @click="onItem(item)"
        >
          <span class="flex-1">{{ item.label }}</span>
        </button>
        <a
          v-else-if="item.href"
          :href="item.href"
          :data-testid="`nav-${item.key}`"
          class="flex min-h-9 items-center gap-2 rounded-lg px-2.5 text-sm no-underline"
          :class="itemClass(item)"
          @click="onItem(item)"
        >
          <span class="flex-1">{{ item.label }}</span>
          <span aria-hidden="true" class="text-xs text-ink-faint">↗</span>
        </a>
        <RouterLink
          v-else-if="item.to"
          :to="item.to"
          :data-testid="`nav-${item.key}`"
          :aria-label="accessibleName(item)"
          :aria-current="item.key === activeKey ? 'page' : undefined"
          class="flex min-h-9 items-center gap-2 rounded-lg px-2.5 text-sm no-underline"
          :class="itemClass(item)"
          @click="onItem(item)"
        >
          <span class="flex-1">{{ item.label }}</span>
          <template v-if="badgeOf(item)">
            <span
              v-if="badgeOf(item)?.text"
              aria-hidden="true"
              :data-testid="`nav-badge-${item.key}`"
              class="rounded-full px-1.5 text-xs font-semibold"
              :class="badgeClass(item)"
              >{{ badgeOf(item)?.text }}</span
            >
            <span
              v-else
              aria-hidden="true"
              :data-testid="`nav-badge-${item.key}`"
              class="h-1.5 w-1.5 rounded-full bg-status-alert"
            />
          </template>
        </RouterLink>
      </template>
    </div>

    <div class="mt-auto flex items-center gap-2.5 border-t border-border-divider px-2 pt-3">
      <span
        aria-hidden="true"
        class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-border-subtle text-xs font-semibold uppercase"
        >{{ currentUser?.email_address?.charAt(0) ?? '?' }}</span
      >
      <span class="min-w-0 flex-1 truncate text-[13px] text-ink-muted" :title="currentUser?.email_address">{{
        currentUser?.email_address
      }}</span>
      <button
        type="button"
        data-testid="sign-out"
        class="min-h-9 rounded-lg px-2 text-[13px] text-ink-muted hover:bg-surface-hover hover:text-ink"
        @click="signOut"
      >
        Sign out
      </button>
    </div>
  </nav>
</template>
